import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Handshake, PlusCircle, Trash2, CheckCircle2, XCircle, Clock, Save, Edit3, X } from "lucide-react";

const TIER_OPTS = [
  { value: "petite", label: "Petite" },
  { value: "classic", label: "Classic" },
  { value: "grand", label: "Grand" },
];

const emptyBundle = () => ({
  name: "", tier: "classic", qty: 10, discount_pct: 30,
  description: "", tag: "", popular: false, active: true,
});

// -------- Bundle CRUD --------
const BundleForm = ({ bundle, onSave, onCancel }) => {
  const [draft, setDraft] = useState(bundle || emptyBundle());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isEdit = !!bundle?.id;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = {
        name: draft.name.trim(),
        tier: draft.tier,
        qty: Number(draft.qty),
        discount_pct: Number(draft.discount_pct),
        description: (draft.description || "").trim(),
        tag: (draft.tag || "").trim(),
        popular: !!draft.popular,
        active: !!draft.active,
      };
      const res = isEdit
        ? await api.patch(`/admin/partners/bundles/${bundle.id}`, payload)
        : await api.post("/admin/partners/bundles", payload);
      onSave(res.data);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="border border-cream-200 rounded-xl p-4 bg-cream-100/60 space-y-3" data-testid={`bundle-${isEdit ? "edit" : "create"}-form`}>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 sm:col-span-1">
          <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Name</label>
          <input required value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            data-testid="bundle-name-input"
            className="mt-1.5 w-full px-3 py-2 rounded-lg border border-cream-200 text-sm bg-white focus:outline-none focus:border-coral-300" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Tier</label>
          <select value={draft.tier} onChange={(e) => setDraft({ ...draft, tier: e.target.value })}
            data-testid="bundle-tier-select"
            className="mt-1.5 w-full px-3 py-2 rounded-lg border border-cream-200 text-sm bg-white focus:outline-none focus:border-coral-300">
            {TIER_OPTS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Qty</label>
          <input type="number" min="1" max="1000" value={draft.qty} onChange={(e) => setDraft({ ...draft, qty: e.target.value })}
            data-testid="bundle-qty-input"
            className="mt-1.5 w-full px-3 py-2 rounded-lg border border-cream-200 text-sm bg-white focus:outline-none focus:border-coral-300" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Discount %</label>
          <input type="number" min="0" max="100" value={draft.discount_pct} onChange={(e) => setDraft({ ...draft, discount_pct: e.target.value })}
            data-testid="bundle-discount-input"
            className="mt-1.5 w-full px-3 py-2 rounded-lg border border-cream-200 text-sm bg-white focus:outline-none focus:border-coral-300" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Badge / tag</label>
          <input value={draft.tag} onChange={(e) => setDraft({ ...draft, tag: e.target.value })}
            placeholder="e.g. Most loved"
            className="mt-1.5 w-full px-3 py-2 rounded-lg border border-cream-200 text-sm bg-white focus:outline-none focus:border-coral-300" />
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Description</label>
        <textarea rows={2} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          className="mt-1.5 w-full px-3 py-2 rounded-lg border border-cream-200 text-sm bg-white focus:outline-none focus:border-coral-300" />
      </div>
      <div className="flex flex-wrap gap-4 text-sm">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!draft.popular} onChange={(e) => setDraft({ ...draft, popular: e.target.checked })} className="w-4 h-4 accent-coral-300" />
          Popular
        </label>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} className="w-4 h-4 accent-coral-300" />
          Active (visible on /vendor-partners)
        </label>
      </div>
      {err && <div className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{err}</div>}
      <div className="flex items-center gap-2">
        <button type="submit" disabled={busy} data-testid="bundle-save-btn" className="inline-flex items-center gap-2 bg-navy-700 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-navy-800 disabled:opacity-60">
          <Save className="w-4 h-4" /> {busy ? "Saving…" : (isEdit ? "Update bundle" : "Create bundle")}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="text-sm text-navy-700/60 hover:text-navy-700 underline">Cancel</button>
        )}
      </div>
    </form>
  );
};

const BundleList = () => {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/admin/partners/bundles");
    setItems(r.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Deactivate this bundle? Existing applications will keep referencing it, but new applicants won't see it on /vendor-partners.")) return;
    await api.delete(`/admin/partners/bundles/${id}`);
    load();
  };

  return (
    <div className="space-y-3" data-testid="partners-bundles-list">
      <div className="flex justify-between items-center">
        <p className="text-xs text-navy-700/60">{items.length} bundle{items.length === 1 ? "" : "s"}</p>
        {!creating && (
          <button onClick={() => setCreating(true)} data-testid="bundle-create-btn"
            className="inline-flex items-center gap-2 text-sm font-semibold text-coral-500 hover:text-coral-600">
            <PlusCircle className="w-4 h-4" /> New bundle
          </button>
        )}
      </div>
      {creating && (
        <BundleForm onCancel={() => setCreating(false)} onSave={(b) => { setCreating(false); setItems(prev => [b, ...prev]); }} />
      )}
      <ul className="divide-y divide-cream-200">
        {items.map(b => (
          <li key={b.id} className="py-3">
            {editingId === b.id ? (
              <BundleForm
                bundle={b}
                onCancel={() => setEditingId(null)}
                onSave={(saved) => { setEditingId(null); setItems(prev => prev.map(x => x.id === saved.id ? saved : x)); }}
              />
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-navy-700">{b.name}</span>
                    <span className="text-[10px] uppercase tracking-wider rounded px-2 py-0.5 bg-coral-100 text-coral-500 font-bold">{b.tier}</span>
                    <span className="text-xs text-navy-700/60">{b.qty} galleries · {b.discount_pct}% off</span>
                    {b.popular && <span className="text-[10px] uppercase tracking-wider rounded px-2 py-0.5 bg-emerald-100 text-emerald-700 font-bold">Popular</span>}
                    {!b.active && <span className="text-[10px] uppercase tracking-wider rounded px-2 py-0.5 bg-navy-700/10 text-navy-700 font-bold">Inactive</span>}
                  </div>
                  {b.description && <div className="text-xs text-navy-700/50 mt-0.5 line-clamp-1">{b.description}</div>}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setEditingId(b.id)} data-testid={`bundle-edit-${b.id}`} className="p-1.5 text-navy-700/60 hover:text-navy-700"><Edit3 className="w-4 h-4" /></button>
                  <button onClick={() => remove(b.id)} data-testid={`bundle-delete-${b.id}`} className="p-1.5 text-red-500/70 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

// -------- Application queue --------
const StatusBadge = ({ status }) => {
  const cfg = {
    pending: { bg: "bg-yellow-100", text: "text-yellow-700", Icon: Clock, label: "Pending" },
    approved: { bg: "bg-emerald-100", text: "text-emerald-700", Icon: CheckCircle2, label: "Approved" },
    rejected: { bg: "bg-red-100", text: "text-red-700", Icon: XCircle, label: "Rejected" },
  }[status] || { bg: "bg-navy-700/10", text: "text-navy-700", Icon: Clock, label: status };
  const { Icon } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-1 ${cfg.bg} ${cfg.text}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
};

const ApplicationRow = ({ app, onDecide }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const decide = async (status) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/partners/applications/${app.id}/decision`, { status, admin_note: note.trim() || null });
      onDecide(r.data);
    } finally { setBusy(false); }
  };

  return (
    <li className="py-3" data-testid={`application-row-${app.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-navy-700">{app.name}</span>
            <span className="text-xs text-navy-700/50">·</span>
            <span className="text-sm text-navy-700/70">{app.company}</span>
            <StatusBadge status={app.status} />
          </div>
          <div className="text-xs text-navy-700/50 mt-0.5">
            {app.email} · {app.category}{app.bundle_name ? ` · wants ${app.bundle_name}` : ""}
          </div>
        </div>
        <button onClick={() => setExpanded(!expanded)} className="text-xs text-navy-700/60 hover:text-navy-700 underline">
          {expanded ? "hide" : "details"}
        </button>
      </div>
      {expanded && (
        <div className="mt-3 border-l-2 border-coral-200 pl-3 space-y-2 text-sm text-navy-700/80">
          {app.message && <div className="italic bg-cream-100 rounded-md px-3 py-2">“{app.message}”</div>}
          {app.admin_note && (
            <div className="text-xs text-navy-700/70">
              Note: <span className="italic">{app.admin_note}</span>
              {app.decided_by && <span className="text-navy-700/40"> — {app.decided_by}</span>}
            </div>
          )}
          {app.status === "pending" && (
            <div className="flex flex-col gap-2 pt-1">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Admin note (optional)"
                data-testid={`application-note-${app.id}`}
                className="w-full px-3 py-2 rounded-lg border border-cream-200 text-sm bg-white focus:outline-none focus:border-coral-300" />
              <div className="flex gap-2">
                <button onClick={() => decide("approved")} disabled={busy}
                  data-testid={`application-approve-${app.id}`}
                  className="inline-flex items-center gap-1.5 bg-emerald-600 text-white px-4 py-1.5 rounded-full text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60">
                  <CheckCircle2 className="w-4 h-4" /> Approve
                </button>
                <button onClick={() => decide("rejected")} disabled={busy}
                  data-testid={`application-reject-${app.id}`}
                  className="inline-flex items-center gap-1.5 bg-red-500 text-white px-4 py-1.5 rounded-full text-sm font-semibold hover:bg-red-600 disabled:opacity-60">
                  <X className="w-4 h-4" /> Reject
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </li>
  );
};

const ApplicationsList = () => {
  const [status, setStatus] = useState("pending");
  const [data, setData] = useState({ items: [], counts: { pending: 0, approved: 0, rejected: 0 } });

  const load = useCallback(async () => {
    const r = await api.get(`/admin/partners/applications?status=${status}`);
    setData(r.data);
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const handleDecide = (updated) => {
    // Remove from current list if it no longer matches the filter, else replace.
    setData(prev => ({
      counts: { ...prev.counts, pending: Math.max(0, prev.counts.pending - 1), [updated.status]: (prev.counts[updated.status] || 0) + 1 },
      items: prev.items.filter(x => x.id !== updated.id),
    }));
  };

  return (
    <div className="space-y-3" data-testid="partners-applications-list">
      <div className="flex flex-wrap gap-2">
        {["pending", "approved", "rejected", "all"].map(s => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            data-testid={`applications-filter-${s}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${status === s ? "bg-navy-700 text-white border-navy-700" : "bg-white text-navy-700 border-cream-200 hover:border-navy-700"}`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
            {s !== "all" && data.counts[s] > 0 && <span className="ml-1.5 opacity-70">({data.counts[s]})</span>}
          </button>
        ))}
      </div>
      {data.items.length === 0 ? (
        <p className="text-sm text-navy-700/50 py-3">No {status !== "all" ? status : ""} applications.</p>
      ) : (
        <ul className="divide-y divide-cream-200">
          {data.items.map(app => <ApplicationRow key={app.id} app={app} onDecide={handleDecide} />)}
        </ul>
      )}
    </div>
  );
};

// -------- Combined Panel --------
const PartnersPanel = () => {
  const [tab, setTab] = useState("bundles");

  return (
    <div data-testid="admin-partners-card" className="bg-white rounded-2xl soft-shadow p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Handshake className="w-5 h-5 text-coral-400" />
        <h2 className="font-semibold text-navy-700 text-lg">Partner bundles &amp; applications</h2>
      </div>
      <p className="text-sm text-navy-700/60 -mt-2">
        Manage the vendor bundle catalog that appears on the public /vendor-partners page, and review incoming partner applications.
      </p>
      <div className="flex gap-2 border-b border-cream-200">
        <button
          onClick={() => setTab("bundles")}
          data-testid="partners-tab-bundles"
          className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === "bundles" ? "border-coral-400 text-coral-500" : "border-transparent text-navy-700/60 hover:text-navy-700"}`}
        >Bundles</button>
        <button
          onClick={() => setTab("applications")}
          data-testid="partners-tab-applications"
          className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === "applications" ? "border-coral-400 text-coral-500" : "border-transparent text-navy-700/60 hover:text-navy-700"}`}
        >Applications</button>
      </div>
      {tab === "bundles" ? <BundleList /> : <ApplicationsList />}
    </div>
  );
};

export default PartnersPanel;
